import React, { useState, useCallback } from "react";
import { Mail, Users } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { FormField } from "./ui/form_field";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "./ui/Modal";
import { useToast } from "./ui/toast";
import { getFormIconClassName } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceSlug: string;
  onInviteSuccess?: (invitation: any) => void;
}

interface InviteFormData {
  email: string;
  role: "member" | "admin";
}

export default function InviteModal({
  isOpen,
  onClose,
  workspaceSlug,
  onInviteSuccess,
}: InviteModalProps) {
  const { showToast } = useToast();
  const [formData, setFormData] = useState<InviteFormData>({
    email: "",
    role: "member",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
      setFormData({ email: "", role: "member" });
      setErrors({});
    }
  }, [isSubmitting, onClose]);

  const handleInputChange = (field: keyof InviteFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await fetch(`/workspaces/${workspaceSlug}/invites`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
        body: JSON.stringify({
          email: formData.email.trim(),
          role: formData.role,
        }),
      });

      const result = await response.json();

      if (result.success) {
        showToast("success", result.message || "Invitation sent successfully!");

        // Call the success callback with the new invitation data
        if (onInviteSuccess && result.invitation) {
          onInviteSuccess(result.invitation);
        }

        handleClose();
      } else {
        if (result.errors && Array.isArray(result.errors)) {
          setErrors({ general: result.errors.join(", ") });
        } else if (result.errors) {
          setErrors(result.errors);
        } else {
          setErrors({
            general: "Failed to send invitation. Please try again.",
          });
        }
      }
    } catch (error) {
      console.error("Error sending invitation:", error);
      setErrors({ general: "Network error. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      canClose={!isSubmitting}
      size="md"
    >
      <ModalHeader
        title="Invite a teammate"
        icon={<Users className="w-6 h-6 text-primary dark:text-primary-light" />}
      />

      <form onSubmit={handleSubmit} className="space-y-4">
        <ModalBody>
          {/* Email Field */}
          <FormField label="Email" error={errors.email}>
            <Input
              type="email"
              placeholder="name@company.com"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              leftIcon={
                <Mail className={getFormIconClassName(!!errors.email)} />
              }
              aria-invalid={!!errors.email}
              disabled={isSubmitting}
              autoFocus
            />
          </FormField>

          {/* Role Selection */}
          <FormField label="Role" error={errors.role}>
            <Select
              value={formData.role}
              onValueChange={(value) => handleInputChange("role", value)}
              disabled={isSubmitting}
            >
              <SelectTrigger
                leftIcon={
                  <Users className={getFormIconClassName(!!errors.role)} />
                }
                aria-invalid={!!errors.role}
              >
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </FormField>

          {/* General Error */}
          {errors.general && (
            <div className="text-red-600 text-sm">{errors.general}</div>
          )}

          {/* Footer Note */}
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Invite expires in 7 days.
          </p>
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || !formData.email.trim()}
          >
            {isSubmitting ? "Sending..." : "Send invite"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
